/**
 * Measurements API Service
 *
 * Uses v2 measurement endpoints from FastAPI backend.
 * Measurements are now separate from installation data.
 */

import { api } from '../client';

export const measurementsService = {
  /**
   * Get a single measurement snapshot for an installation (v2 endpoint).
   * Returns the newest snapshot, or — when `at` is given — the snapshot at that
   * exact measurement_timestamp. One snapshot, not the whole history.
   * @param {string} installationId - Installation ID (UUID)
   * @param {string|null} [at] - ISO timestamp to fetch a specific past frame
   * @returns {Promise<Object|null>} LatestMeasurementResponse with raw_measurements, interpolated_data, etc.
   */
  async getLatest(installationId, at = null) {
    const params = at ? { at } : undefined;
    return await api.get(`/api/v2/measurements/latest/${installationId}`, { params });
  },

  /**
   * Per-cabinet station health (power / network liveness) for an installation,
   * from the edge station_monitor heartbeat. Light; meant to be polled.
   * @param {string} installationId - Installation ID (UUID)
   * @returns {Promise<{installation_id: string, available: boolean, cabinets: Array}>}
   *   available=false => signal source not configured/reachable (render "unknown").
   */
  async getStationHealth(installationId) {
    return await api.get(`/api/v2/measurements/station-health/${installationId}`);
  },

  /**
   * Get measurement history for an installation (v2 endpoint)
   * @param {string} installationId - Installation ID (UUID)
   * @param {number} [limit=10] - Number of snapshots to return
   * @returns {Promise<Array>} Array of MeasurementSnapshot objects
   */
  async getHistory(installationId, limit = 10) {
    return await api.get(`/api/v2/measurements/history/${installationId}`, {
      params: { limit },
    });
  },

  /**
   * Per-cabinet measurement times for the last N snapshots (newest first).
   * Lightweight: only measurement_timestamp + cabinet_status per snapshot, no
   * raw measurements or interpolation grids. Powers the Dashboard's expandable
   * "Latest Measurements" table.
   * @param {string} installationId - Installation ID (UUID)
   * @param {number} [limit=5] - Number of snapshots to return
   * @returns {Promise<Array<{measurement_timestamp: string, cabinet_status: Object|null}>>}
   */
  async getCabinetHistory(installationId, limit = 5) {
    return await api.get(`/api/v2/measurements/cabinet-history/${installationId}`, {
      params: { limit },
    });
  },

  /**
   * Get analytics data for an installation (time-series + spatial snapshots)
   * @param {string} installationId - Installation ID (UUID)
   * @param {string|null} voltageCombo - Voltage combo key filter
   * @param {number} [limit=50] - Number of historical snapshots
   * @returns {Promise<Object>} AnalyticsResponse with available_combos, timeseries, snapshots
   */
  async getAnalytics(installationId, voltageCombo = null, limit = 50) {
    const params = { limit };
    if (voltageCombo) params.voltage_combo = voltageCombo;
    return await api.get(`/api/v2/measurements/analytics/${installationId}`, { params });
  },

  /**
   * Get the latest measurement timestamp for ALL installations in one request.
   * @returns {Promise<Record<string, string>>} { installationId: isoTimestamp }
   */
  async getLatestTimes() {
    return await api.get('/api/v2/measurements/latest-times');
  },

  /**
   * Get polygons/structure for an installation
   * @param {string} installationId - Installation ID
   * @returns {Promise<Array>} Polygon data from installation's structure_polygons field
   */
  async getPolygons(installationId) {
    const response = await api.post('/api/installation', { _id: installationId });
    return response[0]?.structure_polygons || [];
  },
};
